import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Wallet, CreditCard, CheckCircle2, Calendar, Clock, AlertTriangle,
  Download, Receipt, ArrowRight, Loader2, RotateCcw, IndianRupee,
  Landmark, FileText, QrCode,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import useStudentPendingFees from "../../hooks/useStudentPendingFees";
import FeePayFlow from "../QrPayments/FeePayFlow";
import QrPayCard from "../QrPayments/QrPayCard";
import MyQrPaymentsList from "../QrPayments/MyQrPaymentsList";
import CommonToaster from "../Common/CommonToaster";
import Shimmer from "../Common/Shimmer";
import { getMyQrPayments } from "../../utils/qrPaymentApi";
import {
  AppBadge, AppButton, AppCard, AppCardContent, Callout, EmptyState, PageHeader,
  ProgressBar, StatsCard, StatsGrid, Tabs, TabsList, TabsTrigger, TabsContent, cn,
} from "../ui";

// ── helpers ─────────────────────────────────────────────────────────────────
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";
const money = (v) => {
  const n = Number(v) || 0;
  return n.toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: Number.isInteger(n) ? 0 : 2,
  });
};
const titleCase = (s) => (s ? String(s).replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "");

/**
 * Outstanding balance for a plan's enrollment.
 *
 * Derived from total − paid rather than read straight off `ca.pendingFees`:
 * that field is a stored rollup and can lag behind reality (a course added to
 * an existing student used to be written with a `totalFees` but no
 * `pendingFees`, leaving a real ₹20,000 enrollment sitting at 0 and reading as
 * "Fully paid"). The schedule is the second opinion — if installments are still
 * unpaid, the enrollment is not settled no matter what the rollup says.
 */
function planBalance(plan) {
  const ca = plan.courseAssignmentId || {};
  const total = Number(ca.totalFees ?? plan.totalAmount ?? 0) || 0;
  const paid = Number(ca.paidFees ?? 0) || 0;
  const scheduleDue = (plan.installments || []).reduce(
    (s, i) => (i.status === "waived" ? s : s + Math.max((Number(i.amount) || 0) - (Number(i.paidAmount) || 0), 0)),
    0
  );
  return { total, paid, pending: Math.max(Math.max(total - paid, 0), scheduleDue) };
}

const daysUntil = (d) => Math.round((startOfDay(d) - startOfDay(new Date())) / 86400000);

/** Display status (urgency derived from the due date) for one installment. */
function emiView(inst) {
  const amount = Number(inst.amount) || 0;
  const paidAmt = Number(inst.paidAmount) || 0;
  const balance = Math.max(amount - paidAmt, 0);
  const paid = inst.status === "paid";
  const waived = inst.status === "waived";
  const partial = !paid && !waived && paidAmt > 0;
  let key = "upcoming";
  let label = "Upcoming";
  if (paid) { key = "paid"; label = "Paid"; }
  else if (waived) { key = "waived"; label = "Waived"; }
  else {
    const diff = daysUntil(inst.dueDate);
    if (diff < 0) { key = "overdue"; label = "Overdue"; }
    else if (diff === 0) { key = "due"; label = "Due today"; }
    else if (diff <= 7) { key = "due"; label = "Due soon"; }
  }
  if (partial && key !== "overdue") { key = "partial"; label = "Partly paid"; }
  return { key, label, balance, paid, waived, partial };
}

/** Human line for the due banner, e.g. "Due in 3 days" / "Overdue by 5 days". */
function dueLine(dueDate) {
  if (!dueDate) return "Payable now";
  const diff = daysUntil(dueDate);
  if (diff === 0) return "Due today";
  if (diff === 1) return "Due tomorrow";
  if (diff > 1) return `Due ${fmtDate(dueDate)} · in ${diff} days`;
  if (diff === -1) return "Overdue by 1 day";
  return `Overdue by ${-diff} days · was due ${fmtDate(dueDate)}`;
}

const TL_ICON = {
  paid: CheckCircle2,
  waived: CheckCircle2,
  overdue: AlertTriangle,
  due: Calendar,
  partial: Clock,
  upcoming: Clock,
};

/* One installment status drives two surfaces, and each wants a different half of
   the same token family: the rail dot is a tinted circle (`-soft` + `-fg`, the
   §4.4 pill shape), the chip is an AppBadge colour name. A map rather than an
   interpolated class because Tailwind scans source TEXT — a class assembled at
   runtime is never generated and the element renders unstyled. */
const EMI_DOT_CLASSES = {
  paid: "tw-bg-success-soft tw-text-success-fg",
  waived: "tw-bg-success-soft tw-text-success-fg",
  due: "tw-bg-primary/10 tw-text-primary",
  partial: "tw-bg-warning-soft tw-text-warning-fg",
  overdue: "tw-bg-danger-soft tw-text-danger-fg",
  upcoming: "tw-bg-muted tw-text-content-muted",
};

/* `undefined` means "no colour override" — the badge keeps its `secondary`
   variant, which is the neutral grey these statuses had. */
const EMI_CHIP_COLOR = {
  paid: "emerald",
  due: "primary",
  partial: "amber",
  overdue: "rose",
};

const CARD_GRID = "tw-grid tw-grid-cols-[repeat(auto-fill,minmax(min(380px,100%),1fr))] tw-gap-4";

/** Display status for a past payment row. */
function txnView(t) {
  if (t.paymentStatus === "refunded") return { key: "refunded", label: "Refunded" };
  if (t.paymentStatus === "partially_refunded") return { key: "partial", label: "Partly refunded" };
  return { key: "paid", label: "Paid" };
}

// ── page pieces ─────────────────────────────────────────────────────────────

/** The single most urgent thing still owed, above the tabs. */
/**
 * The pay button's words follow how the institute takes money: "Pay by UPI QR"
 * when the QR is the only way, so the student knows what is about to open.
 * `payMode`: 'online' | 'qr' | 'both' | null (nothing to pay with).
 */
function NextPaymentBanner({ due, payMode, onPay }) {
  const isOverdue = due.statusKey === "overdue";
  const heading = isOverdue ? "Payment overdue" : "Next payment";
  return (
    <AppCard
      as="section"
      aria-label={heading}
      className={cn(
        // Wraps only below 680px, where the CTA takes its own full-width line.
        // Above that it is a single nowrap row whose text column absorbs the slack.
        "tw-flex-row tw-items-center tw-gap-4 tw-border-l-4 tw-px-5 tw-py-4",
        "max-[680px]:tw-flex-wrap max-[680px]:tw-px-4 max-[680px]:tw-py-3.5",
        isOverdue ? "tw-border-l-danger" : "tw-border-l-primary",
      )}
    >
      <span
        className={cn(
          "tw-inline-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md",
          isOverdue ? "tw-bg-danger/10 tw-text-danger" : "tw-bg-primary/10 tw-text-primary",
        )}
      >
        {isOverdue ? <AlertTriangle size={20} /> : <Calendar size={20} />}
      </span>

      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-basis-[calc(100%_-_60px)] tw-flex-col tw-gap-0.5 min-[681px]:tw-basis-auto">
        <span
          className={cn(
            "tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-[0.05em]",
            isOverdue ? "tw-text-danger" : "tw-text-primary",
          )}
        >
          {heading}
        </span>
        <span className="tw-break-words tw-text-[15px] tw-font-medium">
          {due.label} — {due.courseName}
        </span>
        <span className="tw-text-[12.5px] tw-text-content-muted">{dueLine(due.dueDate)}</span>
      </div>

      <div className="tw-shrink-0 tw-whitespace-nowrap tw-text-[19px] tw-font-medium min-[681px]:tw-text-[22px]">
        {money(due.balance)}
      </div>

      {payMode ? (
        <AppButton className="tw-shrink-0 max-[680px]:tw-w-full" onClick={onPay}>
          {payMode === "qr" ? <><QrCode /> Pay by UPI QR</> : <><CreditCard /> Pay now <ArrowRight /></>}
        </AppButton>
      ) : (
        <span className="tw-shrink-0 tw-text-[12.5px] tw-text-content-muted">Pay at your institute</span>
      )}
    </AppCard>
  );
}

/** One step of the vertical EMI schedule. */
function EmiRow({ inst, view, isLast, isNext, payMode, onPay }) {
  const Icon = TL_ICON[view.key] || Clock;
  const settled = view.paid || view.waived;
  return (
    <li className="tw-flex tw-min-w-0 tw-gap-3">
      <div className="tw-flex tw-shrink-0 tw-flex-col tw-items-center tw-pt-px">
        <span
          className={cn(
            "tw-inline-flex tw-h-[26px] tw-w-[26px] tw-items-center tw-justify-center tw-rounded-full",
            EMI_DOT_CLASSES[view.key] || EMI_DOT_CLASSES.upcoming,
          )}
        >
          <Icon size={12} />
        </span>
        {!isLast && <span className="tw-my-[3px] tw-min-h-3 tw-w-0.5 tw-flex-1 tw-rounded-[1px] tw-bg-line" />}
      </div>

      <div
        className={cn(
          "tw-min-w-0 tw-flex-1",
          isNext
            ? cn(
                "tw-rounded-md tw-border tw-border-solid tw-px-3 tw-py-2.5",
                isLast ? "tw-mb-0" : "tw-mb-3.5",
                view.key === "overdue"
                  ? "tw-border-danger/30 tw-bg-danger/10"
                  : "tw-border-primary/[0.28] tw-bg-primary/10",
              )
            : cn(isLast ? "tw-pb-0" : "tw-pb-4"),
        )}
      >
        <div className="tw-flex tw-min-w-0 tw-items-baseline tw-justify-between tw-gap-2.5">
          <span
            className={cn(
              "tw-min-w-0 tw-break-words tw-text-[13.5px]",
              settled ? "tw-font-medium tw-text-content-muted" : "tw-font-semibold",
            )}
          >
            {inst.label || `EMI ${inst.seq}`}
          </span>
          <span
            className={cn(
              "tw-shrink-0 tw-whitespace-nowrap tw-text-[13.5px] tw-font-medium",
              settled && "tw-text-content-muted",
            )}
          >
            {money(inst.amount)}
          </span>
        </div>

        <div className="tw-mt-0.5 tw-flex tw-min-w-0 tw-items-baseline tw-justify-between tw-gap-2.5">
          <span className="tw-min-w-0 tw-break-words tw-text-xs tw-text-content-muted">
            {view.paid && inst.paidAt ? `Paid on ${fmtDate(inst.paidAt)}` : fmtDate(inst.dueDate)}
            {view.partial && ` · ${money(inst.paidAmount)} paid, ${money(view.balance)} left`}
          </span>
          <AppBadge variant="secondary" color={EMI_CHIP_COLOR[view.key]} size="sm" className="tw-shrink-0">
            {view.label}
          </AppBadge>
        </div>

        {isNext && payMode && (
          <AppButton size="sm" className="tw-mt-2.5" onClick={onPay}>
            {payMode === "qr" ? <QrCode /> : <IndianRupee />} Pay {money(view.balance)}{payMode === "qr" ? " by UPI QR" : ""}
          </AppButton>
        )}
      </div>
    </li>
  );
}

/** One past payment, with its downloadable invoice. */
function InvoiceRow({ txn, downloading, onDownload }) {
  const view = txnView(txn);
  const net = Math.max(0, (Number(txn.currentPayment) || 0) - (Number(txn.refundAmount) || 0));
  return (
    <AppCard
      as="li"
      className="tw-grid tw-grid-cols-[auto_minmax(0,1fr)_auto_auto] tw-items-center tw-gap-3.5 tw-px-4 tw-py-3 max-[680px]:tw-grid-cols-[auto_minmax(0,1fr)_auto] max-[680px]:tw-gap-y-2.5"
    >
      <span className="tw-inline-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-md tw-bg-primary/10 tw-text-primary">
        <FileText size={16} />
      </span>

      <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
        <span className="tw-break-words tw-text-[13.5px] tw-font-semibold">
          {txn.courseName || "Fee payment"}{txn.batchName ? ` · ${txn.batchName}` : ""}
        </span>
        <span className="tw-break-words tw-text-xs tw-text-content-muted">
          {txn.invoiceNumber} · {fmtDate(txn.date)}{txn.paymentMode ? ` · ${titleCase(txn.paymentMode)}` : ""}
        </span>
      </div>

      <div className="tw-flex tw-flex-col tw-items-end tw-gap-[3px]">
        <span className="tw-whitespace-nowrap tw-text-[14.5px] tw-font-medium">{money(net)}</span>
        <AppBadge variant="secondary" color={view.key === "paid" ? "emerald" : undefined} size="sm">
          {view.key !== "paid" && <RotateCcw size={11} />}
          {view.label}
        </AppBadge>
      </div>

      <AppButton
        variant="outline"
        size="sm"
        className="max-[680px]:tw-col-span-full"
        onClick={onDownload}
        disabled={downloading}
        aria-label={`Download invoice ${txn.invoiceNumber}`}
      >
        {downloading ? <Loader2 className="tw-animate-spin" /> : <Download />}
        Invoice
      </AppButton>
    </AppCard>
  );
}

// ── page ────────────────────────────────────────────────────────────────────
const MyFeesPage = () => {
  const {
    studentId, items, paymentEnabled, qrConfig, qrForFees, loading: pendingLoading, refresh,
  } = useStudentPendingFees();
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [txns, setTxns] = useState([]);
  const [txnsLoading, setTxnsLoading] = useState(true);
  const [tab, setTab] = useState("plans");
  const [payTarget, setPayTarget] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  // This student's own UPI QR payments — waiting, approved, rejected.
  const [qrPayments, setQrPayments] = useState([]);

  const loadQr = useCallback(() => {
    getMyQrPayments()
      .then(({ data }) => setQrPayments(data?.data || []))
      .catch(() => setQrPayments([]));
  }, []);

  useEffect(() => { loadQr(); }, [loadQr]);

  const payMode = paymentEnabled && qrForFees ? "both" : paymentEnabled ? "online" : qrForFees ? "qr" : null;
  const canPay = !!payMode;

  const loadPlans = useCallback(() => {
    setPlansLoading(true);
    apiClient.get("/student/me/fee-plans")
      .then(({ data }) => setPlans(data.plans || []))
      .catch(() => setPlans([]))
      .finally(() => setPlansLoading(false));
  }, []);

  const loadTxns = useCallback(() => {
    setTxnsLoading(true);
    apiClient.get("/student/me/fee-transactions")
      .then(({ data }) => setTxns(data.transactions || []))
      .catch(() => setTxns([]))
      .finally(() => setTxnsLoading(false));
  }, []);

  useEffect(() => { loadPlans(); loadTxns(); }, [loadPlans, loadTxns]);

  const loading = plansLoading || pendingLoading || txnsLoading;

  // Enrollments already covered by an EMI plan (don't repeat them as "pay full").
  const plannedCaIds = useMemo(
    () => new Set(plans.map((p) => String(p.courseAssignmentId?._id || p.courseAssignmentId || ""))),
    [plans]
  );
  const unplannedPending = useMemo(
    () => (items || []).filter(
      (it) => Number(it.pendingFees) > 0 && !plannedCaIds.has(String(it.courseAssignmentId || ""))
    ),
    [items, plannedCaIds]
  );

  // Flat, urgency-sorted list of everything still payable (drives the banner).
  const allDues = useMemo(() => {
    const dues = [];
    for (const plan of plans) {
      const ca = plan.courseAssignmentId || {};
      const caId = ca._id || (typeof plan.courseAssignmentId === "string" ? plan.courseAssignmentId : null);
      const { pending } = planBalance(plan);
      if (pending <= 0) continue;
      const courseName = plan.courseId?.title || plan.courseId?.name || "Course";
      const insts = [...(plan.installments || [])].sort((a, b) => a.seq - b.seq);
      for (const inst of insts) {
        const v = emiView(inst);
        if (v.paid || v.waived || v.balance <= 0) continue;
        dues.push({
          courseAssignmentId: caId,
          courseName,
          label: inst.label || `EMI ${inst.seq}`,
          dueDate: inst.dueDate,
          balance: v.balance,
          pending,
          statusKey: v.key === "partial" ? "due" : v.key,
          rank: v.key === "overdue" ? 0 : v.key === "due" || v.key === "partial" ? 1 : 3,
        });
      }
    }
    for (const it of unplannedPending) {
      dues.push({
        courseAssignmentId: it.courseAssignmentId || null,
        courseName: it.courseName,
        label: "Course fee",
        dueDate: null,
        balance: Number(it.pendingFees),
        pending: Number(it.pendingFees),
        statusKey: "due",
        rank: 2,
      });
    }
    dues.sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      const da = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
      const db = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
      return da - db;
    });
    return dues;
  }, [plans, unplannedPending]);

  // Summary totals.
  const totalPaid = useMemo(
    () => txns.reduce((s, t) => s + Math.max(0, (Number(t.currentPayment) || 0) - (Number(t.refundAmount) || 0)), 0),
    [txns]
  );
  const totalPending = useMemo(
    () => (items || []).reduce((s, it) => s + (Number(it.pendingFees) || 0), 0),
    [items]
  );
  const overdue = useMemo(() => {
    const list = allDues.filter((d) => d.statusKey === "overdue");
    return { amount: list.reduce((s, d) => s + d.balance, 0), count: list.length };
  }, [allDues]);

  const nextDue = allDues[0] || null;
  const hasDues = plans.length > 0 || unplannedPending.length > 0;
  const nothingAtAll = !hasDues && txns.length === 0 && totalPending <= 0 && totalPaid <= 0 && qrPayments.length === 0;

  // Money already sent by QR for an enrolment and not yet decided — it must not
  // be offered again as "owed", or the student pays twice.
  const waitingQr = useMemo(() => qrPayments.filter((p) => p.status === "submitted"), [qrPayments]);
  const waitingFor = useCallback(
    (caId) => waitingQr
      .filter((p) => p.purpose === "fee_payment" && String(p.courseAssignmentId || "") === String(caId || ""))
      .reduce((s, p) => s + (Number(p.amount) || 0), 0),
    [waitingQr]
  );

  // FeePayFlow decides gateway / QR / a choice between them.
  const openPay = useCallback((target) => setPayTarget(target), []);

  // One entry per course owed on, most urgent due first — what the QR card's
  // "upload proof" offers when the student starts from the QR, not a course.
  const qrChoices = useMemo(() => {
    const byCourse = new Map();
    for (const d of allDues) {
      const key = String(d.courseAssignmentId || "student");
      if (!byCourse.has(key)) {
        byCourse.set(key, {
          key,
          courseAssignmentId: d.courseAssignmentId || null,
          courseName: d.courseName,
          pending: d.pending,
          defaultAmount: d.balance,
        });
      }
    }
    return [...byCourse.values()];
  }, [allDues]);

  const handleDownload = useCallback(async (t) => {
    setDownloadingId(t._id);
    try {
      const res = await apiClient.get(`/student/me/fee-transactions/${t._id}/invoice`, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${t.invoiceNumber || "invoice"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      setToast({
        show: true,
        message: e?.response?.data?.message || "Couldn't download the invoice. Please try again.",
        type: "error",
      });
    } finally {
      setDownloadingId(null);
    }
  }, []);

  // ── render pieces ──────────────────────────────────────────────────────────
  const renderTiles = () => (
    <section aria-label="Fee summary">
      <StatsGrid>
        <StatsCard
          icon={Landmark}
          iconColor="primary"
          title="Total fees"
          value={money(totalPaid + totalPending)}
        />
        <StatsCard
          icon={CheckCircle2}
          iconColor="emerald"
          title="Paid so far"
          value={<span className="tw-text-success-fg">{money(totalPaid)}</span>}
        />
        <StatsCard
          icon={Clock}
          iconColor="amber"
          title="Outstanding"
          value={<span className="tw-text-warning-fg">{money(totalPending)}</span>}
        />
        <StatsCard
          icon={AlertTriangle}
          iconColor="rose"
          title={overdue.count > 0 ? `Overdue · ${overdue.count} EMI${overdue.count > 1 ? "s" : ""}` : "Overdue"}
          value={
            overdue.amount > 0
              ? <span className="tw-text-danger-fg">{money(overdue.amount)}</span>
              : money(overdue.amount)
          }
        />
      </StatsGrid>
    </section>
  );

  const renderPlanCard = (plan) => {
    const ca = plan.courseAssignmentId || {};
    const { total, paid, pending } = planBalance(plan);
    const pct = total > 0 ? Math.min(Math.round((paid / total) * 100), 100) : 0;
    const courseName = plan.courseId?.title || plan.courseId?.name || "Course";
    const batchName = ca.batchId?.name || "";
    const installments = [...(plan.installments || [])].sort((a, b) => a.seq - b.seq);
    const paidCount = installments.filter((i) => ["paid", "waived"].includes(i.status)).length;
    const nextSeq = installments.find((i) => {
      const v = emiView(i);
      return !v.paid && !v.waived && v.balance > 0;
    })?.seq;

    return (
      <AppCard as="article" key={plan._id}>
        <AppCardContent className="tw-px-5 tw-py-[18px]">
          <header className="tw-mb-3.5 tw-flex tw-items-start tw-justify-between tw-gap-3">
            <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
              <h3 className="tw-m-0 tw-break-words tw-text-[15.5px] tw-font-medium">{courseName}</h3>
              {batchName && <span className="tw-text-[12.5px] tw-text-content-muted">{batchName}</span>}
            </div>
            {pending <= 0 ? (
              <AppBadge color="emerald" className="tw-shrink-0"><CheckCircle2 size={12} /> Fully paid</AppBadge>
            ) : (
              <AppBadge variant="secondary" className="tw-shrink-0">
                {paidCount}/{installments.length} EMIs paid
              </AppBadge>
            )}
          </header>

          <div className="tw-mb-1.5">
            <ProgressBar value={pct} size="lg" color="emerald" />
            <div className="tw-mt-[7px] tw-flex tw-justify-between tw-gap-2.5 tw-text-xs tw-text-content-muted">
              <span className="tw-text-success-fg">{money(paid)} paid</span>
              <span>{pct}% · {money(total)} total</span>
            </div>
          </div>

          <ol className="tw-m-0 tw-mt-4 tw-flex tw-list-none tw-flex-col tw-p-0">
            {installments.map((inst, idx) => {
              const v = emiView(inst);
              return (
                <EmiRow
                  key={inst.seq}
                  inst={inst}
                  view={v}
                  isLast={idx === installments.length - 1}
                  isNext={inst.seq === nextSeq}
                  payMode={payMode}
                  onPay={() => openPay({
                    courseAssignmentId: ca._id || null,
                    pending,
                    defaultAmount: v.balance,
                    courseName,
                  })}
                />
              );
            })}
          </ol>

          {pending > 0 && !canPay && (
            <p className="tw-mb-0 tw-mt-3 tw-text-[12.5px] tw-text-content-muted">
              Online payment isn't available — please pay at your institute.
            </p>
          )}
        </AppCardContent>
      </AppCard>
    );
  };

  const renderUnplannedCard = (it) => (
    <AppCard as="article" key={it.courseAssignmentId || it.courseName}>
      <AppCardContent className="tw-px-5 tw-py-[18px]">
        <header className="tw-mb-3.5 tw-flex tw-items-start tw-justify-between tw-gap-3">
          <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
            <h3 className="tw-m-0 tw-break-words tw-text-[15.5px] tw-font-medium">{it.courseName}</h3>
            {it.batchName && <span className="tw-text-[12.5px] tw-text-content-muted">{it.batchName}</span>}
          </div>
          <AppBadge color="amber" className="tw-shrink-0">Payment pending</AppBadge>
        </header>

        <div className="tw-mt-1 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
          <div className="tw-flex tw-flex-col tw-gap-0.5">
            <span className="tw-text-[11.5px] tw-uppercase tw-tracking-[0.03em] tw-text-content-muted">
              Pending amount
            </span>
            <span className="tw-text-[22px] tw-font-extrabold">{money(it.pendingFees)}</span>
          </div>
          {canPay ? (
            <AppButton
              onClick={() => openPay({
                courseAssignmentId: it.courseAssignmentId || null,
                pending: Number(it.pendingFees),
                defaultAmount: Number(it.pendingFees),
                courseName: it.courseName,
              })}
            >
              {payMode === "qr" ? <><QrCode /> Pay by UPI QR</> : <><CreditCard /> Pay now</>}
            </AppButton>
          ) : (
            <span className="tw-text-[12.5px] tw-text-content-muted">Pay at your institute</span>
          )}
        </div>
      </AppCardContent>
    </AppCard>
  );

  const renderHistory = () => {
    if (txns.length === 0) {
      return (
        <EmptyState
          icon={<Receipt size={40} />}
          title="No payments yet"
          description="Once you pay a fee, the receipt and downloadable invoice will show up here."
        />
      );
    }
    return (
      <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2.5 tw-p-0">
        {txns.map((t) => (
          <InvoiceRow
            key={t._id}
            txn={t}
            downloading={downloadingId === t._id}
            onDownload={() => handleDownload(t)}
          />
        ))}
      </ul>
    );
  };

  // ── page ──────────────────────────────────────────────────────────────────
  return (
    <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-4 tw-text-content">
      <PageHeader
        icon={Wallet}
        title="My Fees"
        description="Track your fee plan, pay EMIs online and download invoices"
      />

      {loading ? (
        <div className="tw-flex tw-flex-col tw-gap-1">
          <div className="tw-grid tw-grid-cols-4 tw-gap-3 max-[991.98px]:tw-grid-cols-2">
            <Shimmer height={86} />
            <Shimmer height={86} />
            <Shimmer height={86} />
            <Shimmer height={86} />
          </div>
          <Shimmer height={84} />
          <div className={CARD_GRID}>
            <Shimmer height={280} />
            <Shimmer height={280} />
          </div>
        </div>
      ) : nothingAtAll ? (
        <EmptyState
          icon={<Wallet size={40} />}
          title="No fees to show"
          description="You don't have any fee records yet. When your institute sets up your fees, they'll appear here."
        />
      ) : (
        <>
          {renderTiles()}
          {nextDue && (
            <NextPaymentBanner
              due={nextDue}
              payMode={payMode}
              onPay={() => openPay({
                courseAssignmentId: nextDue.courseAssignmentId,
                pending: nextDue.pending,
                defaultAmount: nextDue.balance,
                courseName: nextDue.courseName,
              })}
            />
          )}

          {qrForFees && qrChoices.length > 0 && (
            <QrPayCard
              config={qrConfig}
              blurb={`${qrConfig.qrImageUrl ? "Scan this QR with any UPI app" : "Pay to this UPI ID from any UPI app"}, then upload the screenshot. Your balance updates once your institute approves it.`}
              onUpload={() => setPayTarget({ ...qrChoices[0], method: "qr", choices: qrChoices })}
            />
          )}

          {waitingQr.length > 0 && (
            <Callout tone="info" icon={<QrCode size={16} />}>
              {waitingQr.length === 1
                ? `Your ₹${Number(waitingQr[0].amount).toLocaleString("en-IN")} UPI payment is waiting for your institute to approve it.`
                : `${waitingQr.length} UPI payments are waiting for your institute to approve them.`}
              {" "}Your balance updates once it's approved.
            </Callout>
          )}

          <Tabs value={tab} onChange={setTab}>
            <TabsList>
              <TabsTrigger value="plans">Fee Plans &amp; EMIs</TabsTrigger>
              <TabsTrigger value="history">
                Payment History{txns.length > 0 ? ` (${txns.length})` : ""}
              </TabsTrigger>
              {(qrPayments.length > 0 || qrConfig?.enabled) && (
                <TabsTrigger value="qr">
                  UPI QR Payments{qrPayments.length > 0 ? ` (${qrPayments.length})` : ""}
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="plans" className="tw-mt-4">
              {!hasDues && plans.length === 0 ? (
                <EmptyState
                  icon={<CheckCircle2 size={40} />}
                  title="You're all paid up!"
                  description="You have no pending fees or installments right now."
                />
              ) : (
                <div className={cn(CARD_GRID, "tw-items-start")}>
                  {plans.map(renderPlanCard)}
                  {unplannedPending.map(renderUnplannedCard)}
                </div>
              )}
            </TabsContent>

            <TabsContent value="history" className="tw-mt-4">{renderHistory()}</TabsContent>

            <TabsContent value="qr" className="tw-mt-4">
              <MyQrPaymentsList
                payments={qrPayments}
                onChanged={loadQr}
                onToast={(message, type = "success") => setToast({ show: true, message, type })}
              />
            </TabsContent>
          </Tabs>
        </>
      )}

      <FeePayFlow
        target={payTarget}
        onClose={() => setPayTarget(null)}
        student={{ _id: studentId }}
        paymentEnabled={paymentEnabled}
        qrConfig={qrConfig}
        waitingFor={waitingFor}
        onPaid={() => { refresh(); loadPlans(); loadTxns(); }}
        onQrSubmitted={(payment, message) => {
          if (payment) setQrPayments((list) => [payment, ...list]);
          setToast({ show: true, message: message || "Payment sent for verification.", type: "success" });
        }}
        onBlocked={(message) => setToast({ show: true, message, type: "error" })}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((s) => ({ ...s, show: false }))}
      />
    </div>
  );
};

export default MyFeesPage;
